import React, { useState, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  content: React.ReactNode;
  position?: TooltipPosition;
  delay?: number;
  className?: string;
  children: React.ReactElement;
}

const positionStyles: Record<TooltipPosition, string> = {
  top: 'bottom-full start-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full start-1/2 -translate-x-1/2 mt-2',
  left: 'end-full top-1/2 -translate-y-1/2 me-2',
  right: 'start-full top-1/2 -translate-y-1/2 ms-2',
};

const motionVariants: Record<TooltipPosition, { initial: object; animate: object }> = {
  top: { initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 } },
  bottom: { initial: { opacity: 0, y: -4 }, animate: { opacity: 1, y: 0 } },
  left: { initial: { opacity: 0, x: 4 }, animate: { opacity: 1, x: 0 } },
  right: { initial: { opacity: 0, x: -4 }, animate: { opacity: 1, x: 0 } },
};

export function Tooltip({
  content,
  position = 'top',
  delay = 300,
  className,
  children,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipId = useId();

  const show = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        let top = 0;
        let left = 0;

        switch (position) {
          case 'top':
            top = rect.top + scrollY;
            left = rect.left + scrollX + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + scrollY;
            left = rect.left + scrollX + rect.width / 2;
            break;
          case 'left':
            top = rect.top + scrollY + rect.height / 2;
            left = rect.left + scrollX;
            break;
          case 'right':
            top = rect.top + scrollY + rect.height / 2;
            left = rect.right + scrollX;
            break;
        }

        setCoords({ top, left });
      }
      setVisible(true);
    }, delay);
  }, [delay, position]);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  }, []);

  const mv = motionVariants[position];

  const childProps = children.props as React.HTMLAttributes<HTMLElement>;

  const child = React.cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      childProps.onFocus?.(e);
      show();
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      childProps.onBlur?.(e);
      hide();
    },
    'aria-describedby': visible ? tooltipId : undefined,
  } as React.HTMLAttributes<HTMLElement>);

  const posMap: Record<TooltipPosition, React.CSSProperties> = {
    top: { position: 'absolute', top: coords.top, left: coords.left, transform: 'translate(-50%, -100%) translateY(-8px)' },
    bottom: { position: 'absolute', top: coords.top, left: coords.left, transform: 'translate(-50%, 0) translateY(8px)' },
    left: { position: 'absolute', top: coords.top, left: coords.left, transform: 'translate(-100%, -50%) translateX(-8px)' },
    right: { position: 'absolute', top: coords.top, left: coords.left, transform: 'translate(0, -50%) translateX(8px)' },
  };

  return (
    <>
      {child}
      {createPortal(
        <AnimatePresence>
          {visible && (
            <motion.div
              id={tooltipId}
              role="tooltip"
              initial={mv.initial}
              animate={mv.animate}
              exit={mv.initial}
              transition={{ duration: 0.15 }}
              style={posMap[position]}
              className={cn(
                'z-[9999] pointer-events-none max-w-xs rounded-lg px-3 py-1.5 text-xs font-medium',
                'bg-gray-900 text-white shadow-lg antialiased',
                'dark:bg-gray-100 dark:text-gray-900',
                className,
              )}
            >
              {content}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

Tooltip.displayName = 'Tooltip';
